import type { ParsedUrlQuery } from 'querystring';

import {
    type AlternateLink,
    type ExperimentGroupOrRobots,
    type FullContext,
    type NavItem,
    type Platform,
    type Tag,
} from '@common/clients/api/types.gen';
import { type ExternalSnippetsByPlacement } from '@common/types/ExternalSnippetsByPlacement';
import { type ModulesBySection } from '@common/types/ModulesBySection';
import { type Route } from '@common/types/Route';
import { type SidebarData } from '@common/types/Sidebar';
import { type ThemeID } from '@common/types/ThemeID';
import { type ResolvedPromiseHolder } from '@common/utils/PromiseHolder';

import { type ApiProps } from './ApiProps';
import { type CaptchaProps } from './CaptchaProps';
import { type CrossPlatformByPlatformID } from './CrossPlatformByPlatformID';
import { type RouteParams } from './RouteParams';

export interface CachedContextData {
    context?: FullContext;
    contexts: FullContext[];
    env: Record<string, string>;
    hostname: string;
    platform: Platform;
    config: {
        api: ApiProps;
        socket: ApiProps;
        crossPlatform: CrossPlatformByPlatformID;
        captchaConfig: CaptchaProps;
    };
    topLevelCategories: boolean;
    topLevelPages: boolean;
    topLevelTags: boolean;
}

export interface ContextData extends CachedContextData {
    allowAds: boolean | 'preview';
    allowExternalSnippets: boolean | 'preview';
    allowBetting: boolean | 'preview' | Promise<boolean>;
    allowPremium: boolean;
    allowMyPXR: boolean;
    allowTracking: boolean;
    alternateLinks?: AlternateLink[] | Promise<AlternateLink[]>;
    context: FullContext;
    experimentGroup?: ExperimentGroupOrRobots;
    externalSnippets?: ExternalSnippetsByPlacement | Promise<ExternalSnippetsByPlacement | undefined>;
    isMobileApp?: boolean;
    isPlatformContext: boolean;
    modulesBySection?: ModulesBySection | Promise<ModulesBySection | undefined>;
    route?: Route;
    query?: ParsedUrlQuery;
    path?: string;
    params?: RouteParams;
    sidebarProps?: SidebarData | Promise<SidebarData | undefined>;
    navItems?: NavItem[] | Promise<NavItem[]>;
    premiumSubscriptionTag?: number;
    themeID: ThemeID;
    trendingTags?: Tag[] | Promise<Tag[]>;
}

export type ResolvedContextData = ResolvedPromiseHolder<ContextData>;

export const isContextData = (x: ContextData | any): x is ContextData => !!x?.config?.api?.host;
